import { useState, useEffect, useCallback } from "react";
import {
  collection,
  getDocs,
  updateDoc,
  deleteDoc,
  doc,
  orderBy,
  query,
} from "firebase/firestore";
import { db } from "../firebase";
import "./Orders.scss";
import { CheckCircle, XCircle, RotateCw, Trash2, FileDown } from "lucide-react";
import ExcelJS from "exceljs";
import { saveAs } from "file-saver";

export default function Orders() {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("new");
  const [loading, setLoading] = useState(true);
  const [animatingButton, setAnimatingButton] = useState(null);

  const fetchOrders = useCallback(async () => {
    triggerAnimation("refresh");
    setLoading(true);
    try {
      const q = query(collection(db, "orders"), orderBy("createdAt", "desc"));
      const querySnapshot = await getDocs(q);
      const ordersData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setOrders(ordersData);
    } catch (error) {
      console.error("Error loading orders:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const triggerAnimation = (buttonType) => {
    setAnimatingButton(buttonType);
    setTimeout(() => setAnimatingButton(null), 700);
  };

  const handleStatusChange = async (orderId, newStatus, type) => {
    triggerAnimation(type);
    try {
      const orderRef = doc(db, "orders", orderId);
      await updateDoc(orderRef, { status: newStatus });
      setOrders((prev) =>
        prev.map((o) => (o.id === orderId ? { ...o, status: newStatus } : o))
      );
    } catch (error) {
      console.error("Error updating order:", error);
    }
  };

  const handleDeleteOrder = async (orderId) => {
    if (window.confirm("Opravdu chcete tuto objednávku smazat?")) {
      try {
        await deleteDoc(doc(db, "orders", orderId));
        setOrders((prev) => prev.filter((o) => o.id !== orderId));
      } catch (error) {
        console.error("Error deleting order:", error);
      }
    }
  };

  const handleExportExcel = async (order) => {
    const codacoBlue = "1E3A8A";
    const codacoPrimary = "2563EB";
    const codacoLightBlue = "F0F4FA";
    const codacoBorder = "CBD5E1";

    const workbook = new ExcelJS.Workbook();
    let sheetName = `Obj_${order.orderId}`;
    if (sheetName.length > 31) sheetName = sheetName.slice(0, 31);
    const sheet = workbook.addWorksheet(sheetName);

    const titleRow = sheet.addRow(["Objednávka Codaco e-shop"]);
    titleRow.font = {
      name: "Calibri",
      size: 16,
      bold: true,
      color: { argb: "FFFFFFFF" },
    };
    titleRow.alignment = { horizontal: "center", vertical: "middle" };
    sheet.mergeCells("A1:E1");
    titleRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: codacoBlue },
    };

    sheet.addRow([]);

    const infoRowsData = [
      ["Zákazník", `${order.firstName} ${order.lastName}`],
      ["E-mail", order.email],
      ["Telefon", order.phone || "—"],
      [
        "Datum",
        order.createdAt?.toDate
          ? order.createdAt.toDate().toLocaleString("cs-CZ")
          : "—",
      ],
      ["Číslo objednávky", order.orderId],
      ["Celková částka", `${order.total.toLocaleString("cs-CZ")} Kč`],
    ];

    infoRowsData.forEach(([label, value]) => {
      const row = sheet.addRow([label, value]);

      sheet.mergeCells(`B${row.number}:E${row.number}`);

      const labelCell = row.getCell(1);
      const valueCell = row.getCell(2);

      labelCell.alignment = { horizontal: "left", vertical: "middle" };
      valueCell.alignment = { horizontal: "left", vertical: "middle" };

      labelCell.font = { name: "Calibri", size: 11, bold: false };
      valueCell.font = { name: "Calibri", size: 11, bold: true };
    });

    sheet.addRow([]);

    const sectionTitleRow = sheet.addRow(["Položky objednávky:"]);
    sheet.mergeCells(`A${sectionTitleRow.number}:E${sectionTitleRow.number}`);
    sectionTitleRow.font = { name: "Calibri", size: 11, bold: true };

    sheet.addRow([]);

    const headerRow = sheet.addRow([
      "Produkt",
      "Velikost",
      "Počet",
      "Cena/ks",
      "Celkem",
    ]);

    headerRow.font = { bold: true, color: { argb: "FFFFFFFF" } };
    headerRow.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: codacoPrimary },
    };

    headerRow.getCell(1).alignment = { horizontal: "left" };
    headerRow.getCell(2).alignment = { horizontal: "center" };
    headerRow.getCell(3).alignment = { horizontal: "center" };
    headerRow.getCell(4).alignment = { horizontal: "right" };
    headerRow.getCell(5).alignment = { horizontal: "right" };

    order.items.forEach((item, index) => {
      const row = sheet.addRow([
        item.name,
        item.size,
        item.quantity,
        item.price,
        item.price * item.quantity,
      ]);

      row.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: { argb: index % 2 === 0 ? "FFFFFFFF" : codacoLightBlue },
      };

      row.getCell(1).alignment = { wrapText: true, vertical: "middle" };
      row.getCell(2).alignment = { horizontal: "center", vertical: "middle" };
      row.getCell(3).alignment = { horizontal: "center", vertical: "middle" };
      row.getCell(4).alignment = { horizontal: "right", vertical: "middle" };
      row.getCell(5).alignment = { horizontal: "right", vertical: "middle" };

      row.getCell(4).numFmt = '#,##0 "Kč"';
      row.getCell(5).numFmt = '#,##0 "Kč"';
    });

    sheet.eachRow((row) => {
      row.eachCell((cell) => {
        cell.border = {
          top: { style: "thin", color: { argb: codacoBorder } },
          left: { style: "thin", color: { argb: codacoBorder } },
          bottom: { style: "thin", color: { argb: codacoBorder } },
          right: { style: "thin", color: { argb: codacoBorder } },
        };
      });
    });

    sheet.getColumn(1).width = 40;
    sheet.getColumn(2).width = 10;
    sheet.getColumn(3).width = 8;
    sheet.getColumn(4).width = 15;
    sheet.getColumn(5).width = 15;

    sheet.pageSetup = {
      orientation: "portrait",
      paperSize: 9,
      fitToPage: true,
      fitToWidth: 1,
      fitToHeight: 0,
      margins: {
        left: 0.3,
        right: 0.3,
        top: 0.5,
        bottom: 0.5,
        header: 0.2,
        footer: 0.2,
      },
    };

    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(
      new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      }),
      `objednavka_${order.orderId}.xlsx`
    );
  };

  const filteredOrders = orders.filter(
    (order) => (order.status || "new") === activeTab
  );

  return (
    <section className="orders">
      <h1 className="orders__title">Přehled objednávek</h1>

      <div className="orders__tabs">
        <div className="orders__tabs-group">
          <button
            className={activeTab === "new" ? "active" : ""}
            onClick={() => setActiveTab("new")}
          >
            Nové
          </button>

          <button
            className={activeTab === "completed" ? "active" : ""}
            onClick={() => setActiveTab("completed")}
          >
            Vyřízené
          </button>

          <button
            className={activeTab === "cancelled" ? "active" : ""}
            onClick={() => setActiveTab("cancelled")}
          >
            Storno
          </button>
        </div>

        <button
          onClick={fetchOrders}
          className={`orders__refresh ${
            animatingButton === "refresh" ? "animating" : ""
          }`}
        >
          <RotateCw size={20} className="orders__icon" />
        </button>
      </div>

      {loading ? (
        <p className="orders__loading">Načítám objednávky...</p>
      ) : filteredOrders.length === 0 ? (
        <p className="orders__empty">Zatím žádné objednávky v této sekci.</p>
      ) : (
        <ul className="orders__list">
          {filteredOrders.map((order) => (
            <li key={order.id} className="orders__item">
              <div className="orders__header">
                <h2>
                  {order.firstName} {order.lastName}
                </h2>
                <p>
                  <strong>ID objednávky:</strong> {order.orderId}
                </p>
                <p>
                  <strong>E-mail:</strong> {order.email}
                </p>
                {order.phone && (
                  <p>
                    <strong>Telefon:</strong> {order.phone}
                  </p>
                )}
                <p>
                  <strong>Celková částka:</strong>{" "}
                  {order.total?.toLocaleString("cs-CZ")} Kč
                </p>
                <p>
                  <strong>Datum:</strong>{" "}
                  {order.createdAt?.toDate
                    ? order.createdAt.toDate().toLocaleString("cs-CZ")
                    : "—"}
                </p>
              </div>

              <div className="orders__table-wrapper">
                <table className="orders__table">
                  <thead>
                    <tr>
                      <th>Položka</th>
                      <th>Velikost</th>
                      <th>Počet</th>
                      <th>Cena</th>
                    </tr>
                  </thead>
                  <tbody>
                    {order.items?.map((item, idx) => (
                      <tr key={idx}>
                        <td>{item.name}</td>
                        <td>{item.size}</td>
                        <td>{item.quantity}</td>
                        <td>
                          {(item.price * item.quantity).toLocaleString("cs-CZ")}{" "}
                          Kč
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {order.message && (
                <p className="orders__message">
                  <strong>Poznámka:</strong> {order.message}
                </p>
              )}

              <div className="orders__actions">
                {activeTab === "new" ? (
                  <>
                    <button
                      className={`orders__btn--success ${
                        animatingButton === "success" ? "animating" : ""
                      }`}
                      onClick={() =>
                        handleStatusChange(order.id, "completed", "success")
                      }
                    >
                      <CheckCircle size={18} className="orders__icon" />
                      Vyřízeno
                    </button>

                    <button
                      className={`orders__btn--cancel ${
                        animatingButton === "cancel" ? "animating" : ""
                      }`}
                      onClick={() =>
                        handleStatusChange(order.id, "cancelled", "cancel")
                      }
                    >
                      <XCircle size={18} className="orders__icon" />
                      Storno
                    </button>
                  </>
                ) : (
                  <>
                    <button
                      className="orders__btn--excel"
                      onClick={() => handleExportExcel(order)}
                    >
                      <FileDown size={18} className="orders__icon" />
                      Export Excel
                    </button>

                    <button
                      className="orders__btn--delete"
                      onClick={() => handleDeleteOrder(order.id)}
                    >
                      <Trash2 size={18} className="orders__icon" />
                      Smazat
                    </button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
